/**
 * One order, with the plan and the scheme set side by side.
 *
 * The planner sizes instalments from a goal's arithmetic; the order gateway
 * accepts them on its own terms. Where those disagree, this card shows the
 * disagreement rather than resolving it quietly — four columns, one row per
 * field: what the plan said, what the fund allows, what would actually be sent.
 *
 * A blocked order stays on the screen with its reason attached. Hiding it would
 * leave a goal silently unfunded, and the reason is usually the useful part:
 * "the lock-in outlasts the goal" is a defect in the plan, not in the order.
 *
 * Ported from the user platform's PortfolioPlanner/PlannerOrderCard.tsx. The
 * consent-OTP step it sat behind is gone — an admin acts directly on the
 * investor's behalf — and the debit-day calendar is the planner's own listbox
 * rather than the client site's picker.
 */

import React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReplayIcon from "@mui/icons-material/Replay";
import type {
  OrderReconciliation,
  OrderSelection,
} from "../../../Helpers/portfolioPlanner/orderReconciliation";
import type { PlannerOrder } from "../../../Helpers/portfolioPlanner/types";
import { formatMFInstallmentDay } from "../../../Helpers/mfSchedule";
import { InlineLoader } from "./PlannerFields";
import PlannerSelect from "./PlannerSelect";
import styles from "./portfolio-planner.module.css";

const KIND_LABEL: Record<PlannerOrder["kind"], string> = {
  bondLumpsum: "Bond purchase",
  mutualFundLumpsum: "One-time investment",
  mutualFundSip: "SIP",
};

const VERDICT: Record<
  OrderReconciliation["verdict"],
  { label: string; className: string }
> = {
  checking: { label: "Checking", className: "" },
  unavailable: { label: "Rules unavailable", className: "verdictBlocked" },
  ready: { label: "Ready", className: "verdictReady" },
  adjusted: { label: "Adjusted", className: "verdictAdjusted" },
  "needs-choice": { label: "Needs a choice", className: "verdictChoice" },
  blocked: { label: "Blocked", className: "verdictBlocked" },
};

const OWNER_LABEL: Record<string, string> = {
  plan: "Fix in the plan",
  scheme: "Scheme restriction",
  backend: "Backend gap",
};

export interface PlacementState {
  stage: string;
  message: string;
  error?: string;
  reference?: string;
  /** Set when the failure is one a retry may safely re-run. */
  resumable?: boolean;
  /** What already exists at the gateway, so a retry cannot duplicate it. */
  createdNote?: string;
}

const titleCaseFrequency = (frequency: string): string =>
  frequency ? frequency[0].toUpperCase() + frequency.slice(1) : frequency;

const PlannerOrderCard = ({
  order,
  reconciliation,
  selection,
  placement,
  checked,
  busy,
  onToggle,
  onSelect,
  onPlace,
  onResume,
  resumeLabel,
  placementBlockedReason,
}: {
  order: PlannerOrder;
  reconciliation: OrderReconciliation;
  selection: OrderSelection;
  placement?: PlacementState;
  checked: boolean;
  /** True while any order on the screen is mid-flight. */
  busy: boolean;
  onToggle: () => void;
  onSelect: (patch: OrderSelection) => void;
  onPlace: () => void;
  onResume: () => void;
  /** What the safe retry actually does — it differs by order kind. */
  resumeLabel: string;
  /**
   * Set when nothing on this plan may be placed at all — the investor's MF
   * onboarding is not finished. The reconciliation is still shown: knowing what
   * the plan would place is useful before the KYC is done.
   */
  placementBlockedReason?: string;
}) => {
  const verdict = VERDICT[reconciliation.verdict];
  const placed = placement && placement.stage === "done";
  const running = Boolean(
    placement && placement.stage !== "done" && placement.stage !== "failed"
  );
  const isChecking = reconciliation.verdict === "checking";
  const canResume = Boolean(placement && placement.resumable && !running && !placed);
  // Once anything exists at the gateway for this line, placing it again would
  // create a second one. The only way forward is the resume path.
  const lockedOut = Boolean(placement && placement.createdNote && !canResume && !placed);
  const blocked = Boolean(placementBlockedReason);

  return (
    <li
      className={`${styles.orderCard} ${checked ? "" : styles.orderCardOff} ${
        placed ? styles.orderCardPlaced : ""
      }`}
    >
      <div className={styles.orderHead}>
        <label className={styles.orderPick}>
          <input
            type="checkbox"
            checked={checked}
            /* Anything can be unticked. Disabling a blocked row would trap it
               ticked, and the batch totals would keep counting it. */
            disabled={placed || running}
            onChange={onToggle}
          />
          <span className={styles.orderIdentity}>
            <span className={styles.orderName}>{order.name}</span>
            <span className={styles.orderMeta}>
              <i className={styles.kind}>{KIND_LABEL[order.kind]}</i>
              <span>{order.isin}</span>
              <span>{order.source.name}</span>
            </span>
          </span>
        </label>

        <span className={styles.orderVerdict}>
          {placed ? (
            <span className={`${styles.verdict} ${styles.verdictReady}`}>
              <CheckCircleIcon style={{ fontSize: 14 }} aria-hidden="true" />
              Placed
            </span>
          ) : (
            <span
              className={`${styles.verdict} ${
                verdict.className ? (styles as any)[verdict.className] : ""
              }`}
            >
              {verdict.label}
            </span>
          )}
        </span>
      </div>

      {isChecking ? (
        <p className={styles.orderChecking}>
          <InlineLoader />
          Reading this scheme&apos;s rules…
        </p>
      ) : (
        <>
          {/* The whole point of the screen: what the plan said, what the fund
              allows, and what would actually be sent. */}
          <div className={styles.compareWrap}>
            <table className={styles.compare}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>What the plan says</th>
                  <th>What the fund allows</th>
                  <th>What we would place</th>
                </tr>
              </thead>
              <tbody>
                {reconciliation.rows.map((row) => (
                  <tr
                    key={row.label}
                    className={(styles as any)[`row${row.state}`] || ""}
                  >
                    <td className={styles.compareLabel}>{row.label}</td>
                    <td>{row.planned}</td>
                    <td className={styles.compareAllowed}>{row.allowed}</td>
                    <td className={styles.comparePlacing}>
                      {row.label === "Debit day" &&
                      reconciliation.availableDates.length > 0 &&
                      !placed ? (
                        <span className={styles.dayPicker}>
                          <PlannerSelect<number>
                            value={selection.installmentDay || 0}
                            options={[0].concat(reconciliation.availableDates)}
                            disabled={running}
                            formatOption={(day) =>
                              day
                                ? formatMFInstallmentDay(
                                    reconciliation.frequency || "",
                                    day
                                  )
                                : "Choose a debit day"
                            }
                            onChange={(day) =>
                              onSelect({ installmentDay: day || undefined })
                            }
                          />
                        </span>
                      ) : row.label === "Frequency" &&
                        row.state === "choice" &&
                        !placed ? (
                        <PlannerSelect<string>
                          value={selection.frequency || ""}
                          options={[""].concat(reconciliation.availableFrequencies)}
                          disabled={running}
                          formatOption={(frequency) =>
                            frequency
                              ? titleCaseFrequency(frequency)
                              : "Choose a frequency"
                          }
                          onChange={(frequency) =>
                            onSelect({ frequency: frequency || undefined })
                          }
                        />
                      ) : (
                        row.placing
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {reconciliation.rows
            .filter((row) => row.note)
            .map((row) => (
              <p
                key={`${row.label}-note`}
                className={`${styles.rowNote} ${
                  row.state === "blocked"
                    ? styles.rowNoteBad
                    : row.state === "choice"
                    ? styles.rowNoteChoice
                    : ""
                }`}
              >
                <InfoOutlinedIcon style={{ fontSize: 14, flex: "0 0 auto" }} aria-hidden="true" />
                <span>
                  <b>{row.label}.</b> {row.note}
                </span>
              </p>
            ))}

          {reconciliation.blockers.map((blocker) => (
            <div key={blocker.title} className={styles.blocker}>
              <span className={styles.blockerHead}>
                <WarningAmberIcon style={{ fontSize: 15 }} aria-hidden="true" />
                <b>{blocker.title}</b>
                <i className={styles.blockerOwner}>
                  {OWNER_LABEL[blocker.owner] || blocker.owner}
                </i>
              </span>
              <p>{blocker.detail}</p>
            </div>
          ))}

          {placement && placement.createdNote && !placed ? (
            <p className={`${styles.rowNote} ${styles.rowNoteChoice}`}>
              <InfoOutlinedIcon style={{ fontSize: 14, flex: "0 0 auto" }} aria-hidden="true" />
              <span>{placement.createdNote}</span>
            </p>
          ) : null}

          <div className={styles.orderActions}>
            {placement && placement.error ? (
              <p className={styles.orderError} role="alert">
                {placement.error}
              </p>
            ) : running ? (
              <p className={styles.orderProgress} role="status">
                <span>{placement ? placement.message : "Working…"}</span>
                <span className={styles.progressTrack} aria-hidden="true">
                  <span className={styles.progressIndeterminate} />
                </span>
              </p>
            ) : placed ? (
              <p className={styles.orderPlacedNote}>
                {placement && placement.reference
                  ? `Reference ${placement.reference}. `
                  : ""}
                Creating the order does not fund it — the mandate or the payment
                settles separately.
              </p>
            ) : (
              <span className={styles.orderSpacer} />
            )}

            {!placed && (
              <span className={styles.orderButtons}>
                {canResume && !blocked ? (
                  <button
                    type="button"
                    className={styles.orderPlace}
                    disabled={busy}
                    aria-busy={running || undefined}
                    onClick={onResume}
                  >
                    <ReplayIcon style={{ fontSize: 16 }} aria-hidden="true" />
                    {resumeLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.orderPlace}
                    disabled={
                      !reconciliation.placeable ||
                      busy ||
                      !checked ||
                      lockedOut ||
                      blocked
                    }
                    title={placementBlockedReason}
                    aria-busy={running || undefined}
                    onClick={onPlace}
                  >
                    {running ? (
                      <>
                        <InlineLoader />
                        {placement ? placement.message : "Placing…"}
                      </>
                    ) : (
                      <>
                        Place this order
                        <ArrowForwardIcon style={{ fontSize: 16 }} aria-hidden="true" />
                      </>
                    )}
                  </button>
                )}
              </span>
            )}
          </div>
        </>
      )}
    </li>
  );
};

export default PlannerOrderCard;
